import { Injectable } from '@angular/core';
import {
    map as _map,
    toNumber as _toNumber,
    get as _get,
    isFinite as _isFinite,
} from 'lodash';
import moment from 'moment';
import {
    DeliveryItemInfo,
    DeliveryNoteInput,
    inputMomentFormat,
} from '../models/delivery-note-input';
import { branchList } from 'src/app/shared/config/branch-list';

@Injectable({
    providedIn: 'root',
})
export class MessageService {
    private dateTimeMessageMomentFormat = 'YYYY-MM-DD HH:mm:ss';

    constructor() {}

    fillInputFromMessage(message: any, target: DeliveryNoteInput): void {
        this.fillDocInfo(message, target);
        this.fillCustomerInfo(message, target);
        this.fillProducts(message, target);
    }

    private fillDocInfo(message: any, target: DeliveryNoteInput): void {
        const orderId = _get(message, 'id', '');
        target.docInfo.refNo = orderId;
        if (orderId) {
            // Suggested document number, derived from the order so repeat
            // notes for the same order read as a series (-001, -002, ...).
            // Editable — the -001 is only the starting suffix.
            target.docInfo.invNum = `TDN${orderId}-001`;
        }

        const orderDtFromMsg: string = _get(message, 'order_time', '');
        if (orderDtFromMsg) {
            target.docInfo.date = this.parseMessageDateTime(orderDtFromMsg);
        }
    }

    private fillCustomerInfo(message: any, target: DeliveryNoteInput): void {
        const receiptInfo = _get(message, 'receipt');
        if (receiptInfo) {
            target.customerInfo.name = _get(receiptInfo, 'full_name', '');
            target.customerInfo.taxId = _get(receiptInfo, 'tax_id', '');

            const branchIdFromMsg = _get(receiptInfo, 'branch_id', NaN);
            const branchIdNum: number = _toNumber(branchIdFromMsg);
            if (_isFinite(branchIdNum)) {
                if (branchIdNum < branchList.length) {
                    target.customerInfo.branch = branchList[branchIdNum];
                } else {
                    target.customerInfo.branch = `สาขาที่ ${branchIdFromMsg}`;
                }
            }

            target.customerInfo.useCustomAddress = true;
            target.customerInfo.customAddress = _get(
                receiptInfo,
                'address',
                '',
            );
        } else {
            const receiverInfo = _get(message, 'receiver');
            if (receiverInfo) {
                target.customerInfo.name = _get(receiverInfo, 'full_name', '');

                target.customerInfo.useCustomAddress = false;
                target.customerInfo.postCode = _get(
                    receiverInfo,
                    'zipcode',
                    '',
                );
                target.customerInfo.province = _get(
                    receiverInfo,
                    'province',
                    '',
                );
                target.customerInfo.district = _get(
                    receiverInfo,
                    'district',
                    '',
                );
                target.customerInfo.subDistrict = _get(
                    receiverInfo,
                    'subdistrict',
                    '',
                );
                target.customerInfo.address1 = _get(
                    receiverInfo,
                    'address',
                    '',
                );
            }
        }
    }

    private fillProducts(message: any, target: DeliveryNoteInput): void {
        const productsFromMsg = _get(message, 'products', '');
        if (productsFromMsg) {
            target.items = _map<any, DeliveryItemInfo>(
                productsFromMsg,
                (msgItem, index: number) => {
                    let title: string = _get(msgItem, 'title', '');
                    const sku: string = _get(msgItem, 'code', '');
                    if (sku) {
                        title = `[${sku}] ${title}`;
                    }
                    const quantity: string = _get(msgItem, 'quantity', '');
                    return {
                        itemNo: index + 1,
                        description: title,
                        quantity: _toNumber(quantity),
                        quantityName: '',
                    };
                },
            );
        }
    }

    private parseMessageDateTime(dtFromMsg: string): string {
        const momentObj = moment(dtFromMsg, this.dateTimeMessageMomentFormat);
        if (momentObj.isValid()) {
            return momentObj.format(inputMomentFormat);
        }
        return '';
    }
}
